<template>
  <div>
    <div class="columns">
      <div class="column is-two-thirds">
        <detail-field-2>
          <template v-slot:label>Terminal Name</template>
          <template v-slot:value>{{ terminal?.name || '-' }}</template>
        </detail-field-2>
        <detail-field-2>
          <template v-slot:label>Terminal Serial</template>
          <template v-slot:value>{{ terminal?.serial || '-' }}</template>
        </detail-field-2>
        <detail-field-2>
          <template v-slot:label>Product Code</template>
          <template v-slot:value>{{ terminal?.productCode || '-' }}</template>
        </detail-field-2>
        <detail-field-2>
          <template v-slot:label>Product Name</template>
          <template v-slot:value>{{ terminal?.productName || '-' }}</template>
        </detail-field-2>
        <detail-field-2>
          <template v-slot:label>Owning Account</template>
          <template v-slot:value>{{ getAccountNameFromId(terminal?.accountId) }}</template>
        </detail-field-2>
      </div>
      <div class="column is-one-third" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')">
        <div class="px-4 action-links-container">
          <p><a @click="openEditNameModal">Edit Name</a></p>
          <b-tooltip type="is-danger" :triggers="['click']" :auto-close="['outside']" :active="errorDownloadingOptionFiles" :label="errorMessage" position="is-bottom">
            <p class="mt-2">
              <a v-if="!downloadingOptionsFile" @click="downloadOptionsFile">Download Options File</a>
              <span v-else>Downloading... please wait.</span>
            </p>
          </b-tooltip>
        </div>
      </div>
    </div>
    <hr />
    <div>
      <label class="label">Desired (Kymeta) State</label>
      <div class="columns">
        <div class="column is-one-half">
          <detail-field-2>
            <template v-slot:label>Satellite Provisioned</template>
            <template v-slot:value>
              <span :class="{ 'has-text-success': terminal?.provisioned, 'has-text-grey-dark': !terminal?.provisioned }" class="has-text-weight-semibold">{{ terminal?.provisioned ? 'Yes' : 'No' }}</span>
            </template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>Satellite Activated</template>
            <template v-slot:value>
              <span :class="{ 'has-text-success': terminal?.activated, 'has-text-grey-dark': !terminal?.activated }" class="has-text-weight-semibold">{{ terminal?.activated ? 'Yes' : 'No' }}</span>
            </template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>Beam</template>
            <template v-slot:value>
              <span>{{ terminal?.networkStatus?.beam || 'N/A' }}</span>
            </template>
          </detail-field-2>
          <!-- <detail-field-2>
            <template v-slot:label>Paused</template>
            <template v-slot:value>
              <span :class="{ 'has-text-success': !terminal?.paused, 'has-text-danger': terminal?.paused }" class="has-text-weight-semibold">{{ terminal?.paused ? 'Yes' : 'No' }}</span>
            </template>
          </detail-field-2> -->
        </div>
        <div class="column is-one-half">
          <detail-field-2>
            <template v-slot:label>Cellular Provisioned</template>
            <template v-slot:value>
              <span :class="{ 'has-text-success': terminal?.provisioned, 'has-text-grey-dark': !terminal?.provisioned }" class="has-text-weight-semibold">{{ terminal?.provisioned ? 'Yes' : 'No' }}</span>
            </template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>Cellular Activated</template>
            <template v-slot:value>
              <span :class="{ 'has-text-success': terminal?.activated, 'has-text-grey-dark': !terminal?.activated }" class="has-text-weight-semibold">{{ terminal?.activated ? 'Yes' : 'No' }}</span>
            </template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>Cellular Traffic Enabled</template>
            <template v-slot:value>
              <span :class="{ 'has-text-success': terminal?.enableCellTraffic, 'has-text-grey-dark': !terminal?.enableCellTraffic }" class="has-text-weight-semibold">{{ terminal?.enableCellTraffic ? 'Yes' : 'No' }}</span>
            </template>
          </detail-field-2>
        </div>
      </div>
      <div class="notification is-info is-light" v-if="!terminal?.activeSubscription">
        This terminal does <strong>not</strong> have a subscription. Desired state is <strong>not</strong> NMS state.        
      </div>
    </div>
    <!-- making this hidden as it is not currently wired up by the backend | https://jira.kymetacorp.com/browse/CLDSRV-15344 -->
    <div class="columns is-hidden">
      <div class="column is-one-half">
        <detail-field-2>
          <template v-slot:label>Satellite Usage</template>
          <template v-slot:value>{{terminal?.totalSatelliteUsage ? $numeral(terminal?.totalSatelliteUsage).format('0.00 b') : 'N/A'}}</template>
        </detail-field-2>
      </div>
      <div class="column is-one-half">
        <detail-field-2>
          <template v-slot:label>Cellular Usage</template>
          <template v-slot:value>{{terminal?.totalCellularUsage ? $numeral(terminal?.totalCellularUsage).format('0.00 b') : 'N/A'}}</template>
        </detail-field-2>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, ref, getCurrentInstance } from 'vue';
import { useTerminalsStore } from '../terminals-store';
import getAccountNameFromId from '../../utilities/getAccountNameFromId';

const store = useTerminalsStore();
const terminal = computed(() => store.state.terminal);
const app = getCurrentInstance();

const downloadingOptionsFile = ref(false);
const errorDownloadingOptionFiles = ref(false);
const errorMessage = ref('');

const downloadOptionsFile = async () => {
  errorDownloadingOptionFiles.value = false;
  downloadingOptionsFile.value = true;
  const response = await store.dispatch('downloadOptionsFile', terminal.value);
  downloadingOptionsFile.value = false;
  if (!response.isSuccess) {
    errorDownloadingOptionFiles.value = true;
    errorMessage.value = `Error downloading options file: ${response.error}`;
  }
  // KyAnalytics client plugin, event tracker
  app.proxy.$kya.event('terminals', 'downloadOptionsFile', 'Download optionsFile clicked', { terminalId: terminal.value.id });
}

// #region Update Terminal Modal
const updatingName = ref(false);
const openEditNameModal = () => {
  app.proxy.$buefy.dialog.prompt({
    message: 'Edit Terminal Name',
    inputAttrs: {
      type: 'text',
      placeholder: 'Terminal name...',
      value: terminal.value.name
    },
    confirmText: 'Update Name',
    trapFocus: true,
    closeOnConfirm: false,
    onConfirm: async (value, { close }) => {
      updatingName.value = true;
      await store.dispatch('updateTerminal', { name: value });
      // TODO: make call
      updatingName.value = false;
      app.proxy.$buefy.toast.open(`Terminal name updated successfully!`);
      close();
    }
  })
}
// #endregion
</script>
